import { ShortLinkOrigin } from '../../../domain/short-link/enums.js';

/**
 * The short-link string shape, and the walker that finds what to mint.
 *
 * Pure — no ORM, no session. The domain layer owns persistence; this owns the
 * one definition of what a CABAL short link looks like, so the minter, the
 * resolver, the redirect route and the front-end all read the same rules
 * instead of three regexes that agree until they don't.
 *
 * Shape: `<origin>/l/<target-host>/<code>`
 *
 * The host sits in the path on purpose. A bare code is three tokens cheaper and
 * tells a reader nothing: the model quoting it cannot say where it points, the
 * user hovering it cannot see the destination, and a link pasted out of CABAL
 * becomes an opaque redirect. Carrying the host keeps the destination legible
 * everywhere the string travels, which is the whole reason it is safe to put a
 * link we own in front of a link we don't.
 *
 * NOTHING HERE IS RANDOM ANY MORE (R9, 2026-08-20; reshaped by decision 7,
 * 2026-08-22). A code is DERIVED from what it stands for, so no second
 * variable has to be minted, carried and looked up to say what a printed link
 * means:
 *
 *   a result row's link   the RECORD FINGERPRINT alone — composed by
 *                         `follow-up-addresses.ts` from the record's own facts
 *                         (ref, capture instant, destination, payload).
 *                         Reading one back is a recompute-and-match over the
 *                         reader's live store: the record that produces this
 *                         fingerprint is the record the link was printed for.
 *   any other vendor url  `linkCodeFor({ sessionId, url })` — a fingerprint of
 *                         the destination inside its session. It addresses no
 *                         record because a thumbnail, a PDF or a secondary link
 *                         IS no record; it is a long url wearing a short one.
 *
 * Both are pure functions of their inputs, so minting is idempotent by
 * construction rather than by a read-before-write, and the same input always
 * produces the same published string. Both wear the same clothes: one
 * base64url fingerprint, one width, one charset — a reader cannot tell them
 * apart by looking, and does not need to, because resolution decides (a store
 * record answers for its fingerprint; everything else is a page).
 */

/** The root-level path segment the redirect route mounts on. */
export const SHORT_LINK_PATH_SEGMENT = 'l';

const PRODUCTION_NODE_ENV = 'production';
const HOST_PREFIX_WWW = 'www.';
const HTTP_PROTOCOLS: readonly string[] = ['http:', 'https:'];
// Base64url and nothing else — a code IS a fingerprint (decision 7,
// 2026-08-22: "the '~' is super non standard and shouldn't exist in our minted
// sources"). The `~`-bearing `<token>~<print>` shape that citation codes wore
// before is DEAD, and rejecting it here is the one seam that kills it
// everywhere at once: an old link stops parsing as a short link, so the fetch
// cascade reads it as the page it now is (the ladder), with no compat branch
// anywhere. `{8,}` rather than an exact width so a code minted under an older
// (or later, wider) fingerprint width keeps resolving — resolution is a table
// lookup by the whole string, so width is not load-bearing on the way in.
const CODE_PATTERN = /^[A-Za-z0-9_-]{8,}$/;

/**
 * Where minted links point.
 *
 * Mirrors `resolveCorsOrigins`' environment split rather than introducing an
 * env var: the public origin is already a deployment fact stated in code, and
 * two sources for it is one more than can stay in agreement. If BL stops being
 * reachable at the public origin, this constant is the single place to say so.
 */
export function shortLinkOrigin(input: { nodeEnvironment: string }): string {
  return input.nodeEnvironment === PRODUCTION_NODE_ENV
    ? ShortLinkOrigin.Production
    : ShortLinkOrigin.Development;
}

// ─── codes ──────────────────────────────────────────────────────────────────

/** How many base64url characters a fingerprint is — 8, i.e. 48 bits.
 *
 *  48 bits is the TABLE-WIDE collision guard now that a citation code is the
 *  fingerprint alone (decision 7). The arithmetic (corrected 2026-08-23): two
 *  DIFFERENT records colliding requires a digest accident — the four
 *  fingerprint inputs (ref, capture millisecond, url, payload) agree only on
 *  the same record — and the birthday bound puts the EXPECTED number of
 *  accidental pairs at E[pairs] = n(n-1)/2⁴⁹: E = 0.5 at 16.78M minted codes
 *  (n(n-1) = 2⁴⁸) and E reaches 1 at ~23.73M (n(n-1) = 2⁴⁹). At 1M rows the
 *  chance of even one accident anywhere is ≈ 0.18%. A collision that does
 *  occur cannot publish a wrong destination, because BOTH accident surfaces
 *  refuse: a stored code claiming a different destination is dropped by the
 *  mint seam (never quoted), and a same-batch pair claiming one code for two
 *  destinations is dropped whole before any dedupe (never inserted) — the
 *  affected link stays long and the answer takes the ruled honest path
 *  (decision 6). Widen here, in one place, if the table ever approaches that
 *  population. */
export const CODE_FINGERPRINT_CHARS = 8;

/**
 * The code a plain vendor url is published under, inside one session.
 *
 * Derived rather than random so that minting the same url twice produces the
 * same string: the table's job becomes storing a fact, never allocating one,
 * and a re-found thumbnail cannot end up quoted under two codes. The session is
 * in the digest because the dedup scope is the session — the same destination
 * in two sessions is two published strings, exactly as it was when the code was
 * random.
 *
 * Addresses no record, by construction: it carries no ref, so the `fetch`
 * cascade reads it as a page and nothing about it can be mistaken for a handle.
 */
export function linkCodeFor(input: { sessionId: string; url: string }): string {
  return shortLinkFingerprint(`${input.sessionId} ${input.url}`);
}

/**
 * 48 bits of digest over `text`, as 8 base64url characters.
 *
 * A fingerprint, not a secret and not a checksum: its whole job is to tell two
 * things apart that would otherwise publish the same string. Hand-rolled and
 * dependency-free because this module is imported by the front end, where
 * `node:crypto` does not exist — and because the one property that matters is
 * that BL and FE compute the identical string from the identical input.
 *
 * FNV-1a over UTF-16 units, then two multiply-xorshift rounds so that inputs
 * differing in one millisecond do not differ in one bit.
 */
export function shortLinkFingerprint(text: string): string {
  let hash = FNV_OFFSET_BASIS;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash ^ BigInt(text.charCodeAt(index))) * FNV_PRIME) & U64_MASK;
  }
  hash = (hash ^ (hash >> AVALANCHE_SHIFT)) * AVALANCHE_MULTIPLIER_1 & U64_MASK;
  hash = (hash ^ (hash >> AVALANCHE_SHIFT)) * AVALANCHE_MULTIPLIER_2 & U64_MASK;
  hash ^= hash >> AVALANCHE_SHIFT;
  let code = '';
  for (let shift = FINGERPRINT_TOP_SHIFT; shift >= 0n; shift -= BASE64URL_BITS) {
    code += BASE64URL_ALPHABET[Number((hash >> shift) & BASE64URL_MASK)];
  }
  return code;
}

const FNV_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const U64_MASK = 0xffffffffffffffffn;
const AVALANCHE_SHIFT = 33n;
const AVALANCHE_MULTIPLIER_1 = 0xff51afd7ed558ccdn;
const AVALANCHE_MULTIPLIER_2 = 0xc4ceb9fe1a85ec53n;
const BASE64URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const BASE64URL_BITS = 6n;
const BASE64URL_MASK = 0x3fn;
// The top sextet of the 48 bits this emits: 8 characters × 6 bits.
const FINGERPRINT_TOP_SHIFT = BigInt(CODE_FINGERPRINT_CHARS - 1) * BASE64URL_BITS;

/**
 * The registrable host as it appears in the path.
 *
 * `www.` is dropped because it is noise the reader does not need and the
 * segment is decoration for humans, never the resolve key — resolution is by
 * `code` alone, so a host that normalizes differently later cannot orphan a
 * link that was already published.
 */
export function shortLinkHostOf(input: { url: string }): string | null {
  const parsed = parseHttpUrl({ url: input.url });
  if (parsed === null) return null;
  const host = parsed.hostname.toLowerCase();
  return host.startsWith(HOST_PREFIX_WWW) ? host.slice(HOST_PREFIX_WWW.length) : host;
}

export function buildShortUrl(input: {
  origin: string;
  targetHost: string;
  code: string;
}): string {
  return `${input.origin}/${SHORT_LINK_PATH_SEGMENT}/${input.targetHost}/${input.code}`;
}

/**
 * The code inside one of our short links, or null for anything else.
 *
 * Deliberately origin-agnostic: it matches on the `/l/<host>/<code>` path shape
 * rather than on today's origin, so a link minted before an origin change still
 * resolves. The code is unique table-wide, so the host segment is not consulted.
 */
export function parseShortLinkCode(input: { url: string }): string | null {
  const parsed = parseHttpUrl({ url: input.url });
  if (parsed === null) return null;
  const segments = parsed.pathname.split('/').filter((segment) => segment.length > 0);
  if (segments.length !== 3) return null;
  if (segments[0] !== SHORT_LINK_PATH_SEGMENT) return null;
  const code = segments[2];
  return CODE_PATTERN.test(code) ? code : null;
}

/** True for an absolute http(s) URL that is not already one of ours. */
export function isMintableUrl(input: { value: string }): boolean {
  if (parseHttpUrl({ url: input.value }) === null) return false;
  return parseShortLinkCode({ url: input.value }) === null;
}

function parseHttpUrl(input: { url: string }): URL | null {
  let parsed: URL;
  try {
    parsed = new URL(input.url);
  } catch {
    return null;
  }
  return HTTP_PROTOCOLS.includes(parsed.protocol) ? parsed : null;
}

/**
 * Every mintable URL anywhere in a provider projection, in first-seen order.
 *
 * Structure-blind by design. A projection is ~450 renderer-specific shapes and
 * the field carrying a link is `link` on one, `image` on another, `url` inside
 * a nested offer on a third. Enumerating them would be a per-vendor allowlist
 * that silently misses whatever ships next; walking values means a new provider
 * inherits minting on the day it lands, with no registration step to forget.
 */
export function collectMintableUrls(input: { value: unknown }): string[] {
  const found: string[] = [];
  const seen = new Set<string>();
  walk({ value: input.value, visited: new WeakSet<object>(), found, seen });
  return found;
}

/**
 * The same projection with every occurrence of a listed URL replaced by the
 * next print for that URL, in the traversal order `countUrlOccurrencesIn`
 * counts by — OCCURRENCE-AWARE, not by value (2026-08-23, validation item 3).
 *
 * Two rows of one answer can share one destination while each row's own code
 * must be the one its line prints, so a single string per URL cannot serve:
 * each occurrence consumes the next entry of its URL's print list, and a list
 * shorter than the occurrences repeats its last entry (every entry of one list
 * points at the same destination, so an overflow occurrence is never wrong
 * about WHERE, only about which sibling record it opens — and the print
 * audit's per-row check is what makes any residual pairing drift visible).
 *
 * Returns new containers rather than mutating: the caller renders the result
 * and records it, while the raw projection stays intact for the lane's own
 * classification, which already ran against it.
 */
export function replaceUrlOccurrencesIn(input: {
  value: unknown;
  printsByUrl: ReadonlyMap<string, readonly string[]>;
}): unknown {
  const cursors = new Map<string, number>();
  return rewrite({ value: input.value, printsByUrl: input.printsByUrl, cursors });
}

/**
 * How many times each of `urls` occurs in the projection, by the SAME
 * traversal `replaceUrlOccurrencesIn` rewrites by — the two must agree or an
 * occurrence-aligned print list would pair occurrences with the wrong rows.
 * (Deliberately NOT `collectMintableUrls`' walk: that one dedupes shared
 * sub-objects, while the rewrite copies every encounter.)
 */
export function countUrlOccurrencesIn(input: {
  value: unknown;
  urls: ReadonlySet<string>;
}): Map<string, number> {
  const counts = new Map<string, number>();
  count({ value: input.value, urls: input.urls, counts });
  return counts;
}

interface WalkInput {
  value: unknown;
  visited: WeakSet<object>;
  found: string[];
  seen: Set<string>;
}

function walk(input: WalkInput): void {
  const { value } = input;
  if (typeof value === 'string') {
    if (isMintableUrl({ value }) && !input.seen.has(value)) {
      input.seen.add(value);
      input.found.push(value);
    }
    return;
  }
  if (typeof value !== 'object' || value === null) return;
  // A vendor payload can carry a shared sub-object; without this the walk both
  // duplicates work and, on a cycle, never returns.
  if (input.visited.has(value)) return;
  input.visited.add(value);
  for (const entry of Object.values(value as Record<string, unknown>)) {
    walk({ ...input, value: entry });
  }
}

function count(input: {
  value: unknown;
  urls: ReadonlySet<string>;
  counts: Map<string, number>;
}): void {
  const { value } = input;
  if (typeof value === 'string') {
    if (input.urls.has(value)) input.counts.set(value, (input.counts.get(value) ?? 0) + 1);
    return;
  }
  if (typeof value !== 'object' || value === null) return;
  for (const entry of Object.values(value as Record<string, unknown>)) {
    count({ ...input, value: entry });
  }
}

function rewrite(input: {
  value: unknown;
  printsByUrl: ReadonlyMap<string, readonly string[]>;
  cursors: Map<string, number>;
}): unknown {
  const { value } = input;
  if (typeof value === 'string') {
    const prints = input.printsByUrl.get(value);
    if (prints === undefined || prints.length === 0) return value;
    const at = input.cursors.get(value) ?? 0;
    input.cursors.set(value, at + 1);
    return prints[Math.min(at, prints.length - 1)] ?? value;
  }
  if (typeof value !== 'object' || value === null) return value;
  if (Array.isArray(value)) {
    return value.map((entry) => rewrite({ ...input, value: entry }));
  }
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
      key,
      rewrite({ ...input, value: entry }),
    ]),
  );
}
